// views/Profile/components/DateRangePicker.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Calendar, ChevronDown, Check } from 'lucide-react';
import './DateRangePicker.css';

const PRESETS = [
  { key: 'period', label: 'This period' },
  { key: 'last_7', label: 'Last 7 days' },
  { key: 'last_30', label: 'Last 30 days' },
  { key: 'custom', label: 'Custom' },
];

const labelFor = (preset) =>
  PRESETS.find((p) => p.key === preset)?.label ?? 'This period';

const isoStartOfDay = (yyyymmdd) => {
  if (!yyyymmdd) return null;
  return new Date(`${yyyymmdd}T00:00:00.000Z`).toISOString();
};
const isoEndOfDay = (yyyymmdd) => {
  if (!yyyymmdd) return null;
  return new Date(`${yyyymmdd}T23:59:59.999Z`).toISOString();
};

const computePresetRange = (presetKey) => {
  if (presetKey === 'period') {
    return { since: undefined, until: undefined };
  }
  const days = presetKey === 'last_7' ? 7 : 30;
  const until = new Date();
  const since = new Date(until.getTime() - days * 24 * 60 * 60 * 1000);
  return {
    since: since.toISOString(),
    until: until.toISOString(),
  };
};

/**
 * DateRangePicker — compact dropdown with preset ranges and custom
 * input mode. Reports `{ preset, since, until }` upstream; for the
 * "This period" preset both timestamps are undefined so callers can
 * forward straight to the billing service (which interprets that as
 * the current billing cycle).
 *
 * Props:
 *   value:    { preset, since, until }
 *   onChange: ({ preset, since, until }) => void
 */
export default function DateRangePicker({
  value = { preset: 'period' },
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const [customSince, setCustomSince] = useState('');
  const [customUntil, setCustomUntil] = useState('');
  const containerRef = useRef(null);

  // Close on outside click + ESC.
  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    const onEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const choosePreset = (presetKey) => {
    if (presetKey === 'custom') {
      // Don't fire onChange yet — user has to fill the inputs first.
      return;
    }
    const range = computePresetRange(presetKey);
    onChange?.({ preset: presetKey, ...range });
    setOpen(false);
  };

  const applyCustom = () => {
    if (!customSince || !customUntil) return;
    const since = isoStartOfDay(customSince);
    const until = isoEndOfDay(customUntil);
    if (!since || !until) return;
    if (new Date(since) > new Date(until)) return;
    onChange?.({ preset: 'custom', since, until });
    setOpen(false);
  };

  const triggerLabel =
    value.preset === 'custom' && value.since && value.until
      ? `${value.since.slice(0, 10)} → ${value.until.slice(0, 10)}`
      : labelFor(value.preset ?? 'period');

  return (
    <div className="date-range-picker" ref={containerRef}>
      <button
        type="button"
        className="date-range-picker__trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Calendar size={14} aria-hidden="true" />
        <span>{triggerLabel}</span>
        <ChevronDown size={14} aria-hidden="true" />
      </button>

      {open && (
        <div className="date-range-picker__panel" role="listbox">
          {PRESETS.map((p) => {
            const selected = (value.preset ?? 'period') === p.key;
            return (
              <button
                key={p.key}
                type="button"
                className={`date-range-picker__option${
                  selected ? ' date-range-picker__option--selected' : ''
                }`}
                onClick={() => choosePreset(p.key)}
                role="option"
                aria-selected={selected}
              >
                {p.label}
                {selected && <Check size={14} aria-hidden="true" />}
              </button>
            );
          })}

          {(value.preset === 'custom' ||
            (value.preset !== 'custom' &&
              PRESETS.find((p) => p.key === 'custom'))) && (
            <div className="date-range-picker__custom">
              <label className="date-range-picker__field">
                <span>From</span>
                <input
                  type="date"
                  value={customSince}
                  onChange={(e) => setCustomSince(e.target.value)}
                />
              </label>
              <label className="date-range-picker__field">
                <span>To</span>
                <input
                  type="date"
                  value={customUntil}
                  onChange={(e) => setCustomUntil(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="date-range-picker__apply"
                onClick={applyCustom}
                disabled={!customSince || !customUntil}
              >
                Apply
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
